import React from "react";
import { formatPlAmount } from "../../utils/formatPlAmount";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const revenueColor = "bg-[#36d7b7]";
const expensesColor = "bg-[#ff7675]";

export interface RevenueVsExpensesChartProps {
  title: string;
  data: Array<{
    monthLabel: string;
    revenue: number;
    expenses: number;
    ebitda: number;
  }>;
}

function RevenueExpenseComboChart({
  data,
  valueFormatter,
}: {
  data: RevenueVsExpensesChartProps["data"];
  valueFormatter: (value: number) => string;
}) {
  return (
    <div className="h-full min-h-[320px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis
            dataKey="monthLabel"
            tick={{ fill: "#6b7280", fontSize: 12 }}
            tickLine={{ stroke: "#e5e7eb" }}
          />
          <YAxis
            tick={{ fill: "#6b7280", fontSize: 12 }}
            tickLine={{ stroke: "#e5e7eb" }}
            tickFormatter={(value) => valueFormatter(Number(value))}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: "#ffffff",
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              fontSize: "12px",
            }}
            formatter={(value, _name, item) => {
              const dataKey = String(
                (item as { dataKey?: string | number } | undefined)?.dataKey ??
                  "",
              );
              const seriesName =
                dataKey === "revenue"
                  ? "Revenue"
                  : dataKey === "expenses"
                    ? "Expenses"
                    : dataKey === "ebitda"
                      ? "EBITDA"
                      : dataKey;
              return [valueFormatter(Number(value)), seriesName];
            }}
            labelFormatter={(label) => String(label)}
          />
          <Bar
            dataKey="revenue"
            name="Revenue"
            fill="#36d7b7"
            radius={[4, 4, 0, 0]}
            barSize={18}
            maxBarSize={22}
          />
          <Bar
            dataKey="expenses"
            name="Expenses"
            fill="#ff7675"
            radius={[4, 4, 0, 0]}
            barSize={18}
            maxBarSize={22}
          />
          <Line
            type="monotone"
            dataKey="ebitda"
            name="EBITDA"
            stroke="#f59e0b"
            strokeWidth={1.75}
            dot={{ fill: "#f59e0b", r: 3 }}
            activeDot={{ r: 5 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RevenueVsExpensesChart({
  title,
  data,
}: RevenueVsExpensesChartProps) {
  return (
    <div className="flex min-w-0 flex-1 h-full flex-col justify-center items-start gap-6">
      <div className="flex w-full items-center justify-between">
        <span className="text-caption-bold font-caption-bold text-neutral-500 uppercase tracking-wide">
          {title}
        </span>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <div
              className={`flex h-3 w-3 flex-none flex-col items-start rounded-sm ${revenueColor}`}
            />
            <span className="text-caption font-caption text-neutral-600">
              Revenue
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div
              className={`flex h-3 w-3 flex-none flex-col items-start rounded-sm ${expensesColor}`}
            />
            <span className="text-caption font-caption text-neutral-600">
              Expenses
            </span>
          </div>
          <div className="flex items-center gap-2">
            <div className="h-0 w-4 border-t-2 border-[#f59e0b]" />
            <span className="text-caption font-caption text-neutral-600">
              EBITDA
            </span>
          </div>
        </div>
      </div>
      <RevenueExpenseComboChart
        data={data}
        valueFormatter={(value: number) => formatPlAmount(value)}
      />
    </div>
  );
}
